import React, { useEffect, useRef } from 'react';
import './HomeAbout.css';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { gsap } from 'gsap';

const HomeAbout = () => {
  const sectionRef = useRef(null);
  const imagesRef = useRef([]);
  const labelRef = useRef(null);
  const headingRef = useRef(null);
  const paragraphsRef = useRef([]);
  const buttonRef = useRef(null);
  const particlesRef = useRef([]);
  const featuresRef = useRef([]);
  const leftImageRef = useRef(null);
  const rightImageRef = useRef(null);

  // Function to scroll to Form section
  const scrollToForm = () => {
    const formSection = document.getElementById('form-section');
    if (formSection) {
      formSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Handle Get in Touch button click
  const handleGetInTouch = () => {
    scrollToForm();
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Section background gradient animation
      gsap.fromTo(sectionRef.current,
        { 
          background: "linear-gradient(135deg, #0a0a0b 0%, #1D1E28 50%, #0a0a0b 100%)" 
        },
        { 
          background: "linear-gradient(135deg, #0a0a0b 0%, #1D1E28 70%, #0a0a0b 100%)",
          duration: 3,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut"
        }
      );

      // Image animations - VISIBLE FROM START
      imagesRef.current.forEach((img, index) => {
        if (!img) return;
        
        // Start with visible images
        gsap.set(img, { 
          opacity: 0.4,
          scale: 1,
          rotation: index === 0 ? -10 : 10 
        });
      });

      // Stagger text animations
      gsap.utils.toArray([
        labelRef.current,
        headingRef.current,
        ...paragraphsRef.current,
        ...featuresRef.current,
        buttonRef.current
      ]).forEach((el, index) => {
        if (el) {
          gsap.fromTo(el,
            {
              opacity: 0,
              y: 20
            },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: "power3.out",
              delay: 0.5 + (index * 0.15)
            }
          );
        }
      });

      // Continuous floating animation for images
      imagesRef.current.forEach((img, index) => {
        if (!img) return;
        gsap.to(img, {
          y: "+=15",
          duration: 4,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
          delay: index * 0.5
        });
      });

      // Particle animations
      particlesRef.current.forEach((particle, index) => {
        if (particle) {
          gsap.to(particle, {
            x: Math.random() * 100 - 50,
            y: Math.random() * 100 - 50,
            rotation: Math.random() * 360,
            duration: 10 + Math.random() * 10,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
            delay: index * 0.2
          });
        }
      });

      // Label line animation
      const labelLines = document.querySelectorAll('.label-line');
      gsap.to(labelLines, {
        scaleX: 1.2,
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        stagger: 0.3
      });

      // Underline animation
      const underline = document.querySelector('.label-underline');
      gsap.to(underline, {
        scaleX: 1.3,
        duration: 1.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      // Button hover state animation
      const button = buttonRef.current;
      if (button) {
        gsap.to(button, {
          scale: 1.02,
          boxShadow: "0 15px 35px rgba(189, 239, 68, 0.3)",
          duration: 2,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut"
        });
      }
    });

    return () => ctx.revert();
  }, []);

  const addImageRef = (el) => {
    if (el && !imagesRef.current.includes(el)) {
      imagesRef.current.push(el);
    }
  };

  const addParticleRef = (el) => {
    if (el && !particlesRef.current.includes(el)) {
      particlesRef.current.push(el);
    }
  };

  const addFeatureRef = (el) => {
    if (el && !featuresRef.current.includes(el)) {
      featuresRef.current.push(el);
    }
  };

  // Features data
  const features = [
    "Stable four wheel platforms",
    "Low center of gravity for rider confidence",
    "Hydraulic disc braking systems",
    "Foldable design for transport and storage",
    "Electric drivetrains with minimal service requirements"
  ];

  return (
    <section className="home-about-section" ref={sectionRef}>
      {/* Animated Background Particles */}
      <div className="particles-container">
        {[...Array(15)].map((_, index) => (
          <div
            key={index}
            ref={addParticleRef}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
              opacity: Math.random() * 0.1 + 0.05,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      {/* Left Decorative Image */}
      <div className="decorative-image left-image" ref={leftImageRef}>
        <img 
          ref={(el) => addImageRef(el)}
          src="./bike1.png" 
          alt="EZ Raider Decoration Left" 
          className="decorative-img"
        />
      </div>
      
      {/* Right Decorative Image */}
      <div className="decorative-image right-image" ref={rightImageRef}>
        <img 
          ref={(el) => addImageRef(el)}
          src="./bike2.png" 
          alt="EZ Raider Decoration Right" 
          className="decorative-img mirrored"
        />
      </div>

      {/* Main Content Container */}
      <div className="about-container">
        {/* About Us Label */}
        <div className="about-label" ref={labelRef}>
          <div className="label-line left"></div>
          <span className="label-text">ABOUT EZ RAIDER</span>
          <div className="label-line right"></div>
          <div className="label-underline"></div>
        </div>

        {/* Main Heading */}
        <h1 className="about-heading" ref={headingRef}>
          <span className="heading-line">RIDE WITH PURPOSE</span>
          <span className="heading-line accent">ELEVATE YOUR JOURNEY</span>
        </h1>

        {/* Description Paragraphs */}
        <div className="about-description">
          <p 
            ref={(el) => paragraphsRef.current[0] = el}
            className="description-text"
          >
            EZ Raider vehicles introduce a new category of electric mobility focused on stability, simplicity, and terrain adaptability. Each model is designed to be intuitive to operate, reliable in demanding environments, and easy to maintain.
          </p>
          <p 
            ref={(el) => paragraphsRef.current[1] = el}
            className="description-text"
          >
            Across the lineup, EZ Raider vehicles offer:
          </p>
        </div>

        {/* Features Grid - 3 columns */}
        <div className="features-grid">
          {/* First Row - 3 items */}
          <div className="features-row">
            {features.slice(0, 3).map((feature, index) => (
              <div 
                key={index}
                ref={addFeatureRef}
                className="feature-item"
              >
                <span className="feature-dash">—</span> {feature}
              </div>
            ))}
          </div>
          
          {/* Second Row - 2 items */}
          <div className="features-row">
            {features.slice(3, 5).map((feature, index) => (
              <div 
                key={index + 3}
                ref={addFeatureRef}
                className="feature-item"
              >
                <span className="feature-dash">—</span> {feature}
              </div>
            ))}
          </div>
        </div>

        {/* Call-to-Action Button */}
        <button 
          ref={buttonRef}
          className="cta-button" 
          onClick={handleGetInTouch}
        >
          <span className="button-text">GET IN TOUCH</span>
          <ArrowForwardIcon className="button-icon" />
        </button>
      </div>
    </section>
  );
};

export default HomeAbout;